import * as React from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { Box, Chip, Checkbox, ListItemText, CircularProgress } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { GetFontAwesomeIcon } from "@/components/font-awesome-icon";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { useSelector } from "react-redux";
import { getTagColors } from "@/store/slices/general/dropdownOptionsSlice";
import TruncateTooltip from "@/components/truncate-with-tooltip";
const getTextFromReactElement = (element) => {
  if (!element) return "";
  if (typeof element === "string" || typeof element === "number") {
    return String(element);
  }
  if (Array.isArray(element)) {
    return element.map(getTextFromReactElement).filter(Boolean).join(" ");
  }
  if (element.props) {
    if (element.props.children) {
      return getTextFromReactElement(element.props.children);
    }
  }
  return "";
};

const UISelect = ({
  name,
  value,
  error,
  title,
  onChange,
  onBlur = () => console.log("blur"),
  options = [],
  size = "small",
  clearable = true,
  clearableLabel = "None",
  placeholderOnly = false,
  disabled = false,
  multiple = false,
  tooltip = "",
  required = false,
  optionTooltip = "",
  helperTextStyles = {},
  sx = {},
  selectedSlotLabel = "",
  noDataText = "No option available",
  showNoData = false,
  menuSX = {},
  loading = false,
  wrapperClass = null,
  menuItemSx = {},
  renderChip,
  renderMenuChip,
  checkboxItems = false,
  renderChips = false,
  chipDeletable = false,
  renderEmptyValue = true,
  variant = "outlined",
  showErrorText = false,
}) => {
  const [focused, setFocused] = React.useState(false);
  // Must stay in sync with InputLabel `shrink` and Select `label` so the
  // floating label is always offset above the rendered value (no overlap).
  const hasRenderedValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== "" && value !== null && value !== undefined ||
      ((selectedSlotLabel || renderEmptyValue));
  const isLabelShrunk = hasRenderedValue || focused;
  const tagColors = useSelector(getTagColors);
  const renderValue = (selected) => {
    if (multiple) {
      if (renderChips) {
        if (Array.isArray(selected) && selected.length > 0) {
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((val) => {
                const opt = options.find((o) => o.value === val);
                return (
                  <Chip
                    key={val}
                    label={opt?.label ?? val}
                    size="small"
                    {...(chipDeletable
                      ? {
                          onDelete: (e) => {
                            e.stopPropagation();
                            const newValue = (Array.isArray(value) ? value : []).filter(
                              (v) => v !== val
                            );
                            onChange({ target: { name, value: newValue } });
                          },
                          onMouseDown: (e) => e.stopPropagation(),
                        }
                      : {})}
                  />
                );
              })}
            </Box>
          );
        }
        return null;
      }
      if (Array.isArray(selected) && selected.length > 0) {
        const labels = selected
          .map((id) => options.find((opt) => opt.value === id)?.label)
          .filter(Boolean);
        const joinedLabels = labels.join(", ");
        return (
          <span
            title={joinedLabels}
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}
          >
            {joinedLabels}
          </span>
        );
      }
      return placeholderOnly ? (
        <Typography variant="allVariants" sx={{ color: "#a2a2a2" }}>
          {title + (required ? " *" : "")}
        </Typography>
      ) : null;
    } else {
      const matchingOption = options.find((val) => val.value === selected);
      if (matchingOption) {
        return renderChip ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {renderChipOption(matchingOption)}
          </Box>
        ) : (
          <span
            title={getTextFromReactElement(matchingOption.label) || String(matchingOption.value || "")}
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}
          >
            {matchingOption.label}
          </span>
        );
      } else if ((selected === "" || selected === null || selected === undefined) && renderEmptyValue) {
        return (
          <span
            title={clearableLabel}
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}
          >
            {clearableLabel}
          </span>
        );
      } else if (selectedSlotLabel) {
        return (
          <Typography
            variant="allVariants"
            sx={{
              color: disabled
                ? "var(--palette-disabled_button_text)"
                : "red",
            }}
          >
            {selectedSlotLabel}
          </Typography>
        );
      } else if (placeholderOnly) {
        return (
          <Typography variant="allVariants" sx={{ color: "#a2a2a2" }}>
            {title + (required ? " *" : "")}
          </Typography>
        );
      }
      return null;
    }
  };

  const renderChipOption = (option) => {
    let filterColor = {
      code: "var(--palette-default_bg)",
      textCode: "var(--palette-default)",
    };
    if (option.color) {
      filterColor = tagColors.find((color) => color?.id == option.color);
    }
    return (
      <Chip
        size={size}
        key={option.id}
        label={
          <Typography
            display="flex"
            alignItems="center"
            sx={{ fontSize: "0.8rem" }}
          >
            {option.icon && option.icon}
            <TruncateTooltip text={option.label} limit={15} />
          </Typography>
        }
        sx={{
          backgroundColor: `${filterColor?.code}`,
          color: `${filterColor?.textCode}!important`,
          WebkitTextFillColor: "initial",
        }}
      />
    );
  };

  return (
    // <FormControl fullWidth size="small">
    //   <Select
    //     name={name}
    //     value={value}
    //     onChange={onChange}
    //     variant="outlined"
    //     renderValue={(selected) =>
    //       selected ? (
    //         options.find((val) => val.value == selected).label
    //       ) : (
    //         <em>Placeholder</em>
    //       )
    //     }
    //   >
    //     {options &&
    //       options.map((opt, index) => (
    //         <MenuItem key={index} value={opt.value}>
    //           {opt.label}
    //         </MenuItem>
    //       ))}
    //   </Select>
    // </FormControl>
    <>
      <Tooltip title={tooltip} placement="top-end">
        <div className={wrapperClass}>
          <FormControl
            fullWidth
            error={!!error}
            size={size}
            variant={variant}
            sx={{
              ...sx,
              maxWidth: "100%",
              bgcolor: disabled
                ? "var(--palette-disabled_button)"
                : "var(--palette-grid_table)",
              "& .Mui-focused": {
                color: "var(--palette-grey_text) !important",
              },
            }}
          >
            <InputLabel
              id={`${name}-select`}
              shrink={isLabelShrunk}
              variant={variant}
              sx={{
                color: disabled
                  ? "var(--palette-disabled_button_text)"
                  : "var(--palette-grey_text)",
              }}
            >
              {!placeholderOnly ? title + (required ? " *" : "") : null}
            </InputLabel>
            <Select
              sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight: "32px !important",
                  // color: "var(--base-140)", // Set the desired color for the underline here
                  color: disabled
                    ? "var(--palette-disabled_button_text) !important"
                    : "var(--palette-text)",
                  WebkitTextFillColor: disabled
                    ? "var(--palette-disabled_button_text) !important"
                    : "var(--palette-text)",
                },
                "&.Mui-disabled": {
                  cursor: "default",
                },
                ...(renderChip && value ? {
                  "& .MuiSelect-outlined": {
                    padding: "10px",
                  }
                } : {}),
                "&.Mui-disabled .MuiSelect-select": {
                  WebkitTextFillColor:
                    "var(--palette-disabled_button_text) !important",
                  color: "var(--palette-disabled_button_text) !important",
                },
              }}
              disabled={disabled}
              multiple={multiple}
              size={size}
              variant={variant}
              // labelId={`${name}-select`}
              MenuProps={{
                PaperProps: {
                  className: "custom-scroll",
                  style: {
                    maxHeight: "250px",
                    maxWidth: "300px",
                    boxShadow: "0px 1px 6px #05060F26",
                    ...menuSX,
                  },
                  sx: {
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "transparent !important",
                      color: "var(--palette-secondary_button_text) !important",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "var(--palette-secondary_button_hover)",
                    },
                    "& .MuiMenuItem-root.Mui-selected:hover": {
                      backgroundColor:
                        "var(--palette-secondary_button_hover) !important",
                    },
                  },
                },
              }}
              id={"txt-livechat-select" + name}
              name={name}
              value={multiple ? value || [] : value}
              onChange={onChange}
              onFocus={() => setFocused(true)}
              onBlur={(e) => {
                setFocused(false);
                if (onBlur) onBlur(e);
              }}
              notched={isLabelShrunk}
              label={
                !placeholderOnly
                  ? title + (required ? " *" : "")
                  : null
              }
              displayEmpty={placeholderOnly || renderEmptyValue ? true : false}
              renderValue={(selected) => renderValue(selected)}
              IconComponent={(props) =>
                loading ? (
                  <CircularProgress
                    className={props.className}
                    size={16}
                    thickness={5}
                    sx={{ color: "var(--palette-grey_text)" }}
                  />
                ) : (
                  <GetFontAwesomeIcon
                    icon={faChevronDown}
                    iconStyles={{
                      fontSize: "0.625rem",
                      color: "var(--palette-grey_text)",
                      display: disabled ? "none" : undefined,
                    }}
                    {...props}
                  />
                )
              }
            >
              {!required && clearable ? (
                <MenuItem value="">{clearableLabel}</MenuItem>
              ) : null}
              {loading ? (
                <MenuItem value="">Loading..</MenuItem>
              ) : !!options?.length ? (
                options.map((option, index) =>
                  option.disabled ? (
                    <Tooltip
                      title={option.tooltip || ""}
                      placement="top"
                      arrow
                      key={index}
                    >
                      <div>
                        <MenuItem
                          value={option.value}
                          disabled={option.disabled}
                          sx={{
                            color: option.disabled
                              ? "var(--palette-disabled_button_text)"
                              : "var(--palette-text)",
                            opacity: "1 !important",
                          }}
                        >
                          <span style={{ width: "100%" }}>
                            {option.label}&nbsp;{option.helperText}
                          </span>
                        </MenuItem>
                      </div>
                    </Tooltip>
                  ) : (
                    <MenuItem
                      value={option.value}
                      key={index}
                      disabled={option.disabled}
                      sx={{
                        color: option.disabled
                          ? "var(--palette-disabled_button_text)"
                          : "var(--palette-text)",
                        opacity: "1 !important",
                      }}
                    >
                      {checkboxItems ? (
                        <>
                          <Checkbox
                            checked={Array.isArray(value) && value.indexOf(option.value) > -1}
                            size="small"
                            sx={{ p: 0, mr: 1 }}
                          />
                          <ListItemText primary={option.label} />
                        </>
                      ) : renderMenuChip ? (
                        renderChipOption(option)
                      ) : (
                        <Box
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            ...menuItemSx,
                            width: "100%",
                          }}
                          title={
                            option.title ||
                            (typeof option.label === "string"
                              ? option.label
                              : getTextFromReactElement(option.label) || undefined)
                          }
                        >
                          {option.label}
                          {option.helperText && ` `}
                          {option.helperText}
                        </Box>
                      )}
                    </MenuItem>
                  ),
                )
              ) : showNoData ? (
                <MenuItem
                  value={"nothing to show"}
                  key={"NoOption"}
                  sx={{ textAlign: "center" }}
                  disabled={true}
                >
                  {noDataText}
                </MenuItem>
              ) : null}
            </Select>
            <FormHelperText
              sx={{
                position: helperTextStyles?.position || "absolute",
                bottom: helperTextStyles?.bottom || "-20px", 
                lineHeight: helperTextStyles?.lineHeight || 1.66,
                ...helperTextStyles,
              }}
            >
              {error}
            </FormHelperText>
          </FormControl>
        </div>
      </Tooltip>
    </>
  );
};
export default UISelect;
